/**
 * Crown Jewel Asset Manager Component
 * 
 * This component serves as the main interface for managing crown jewel assets.
 * It provides functionality for:
 * - Client selection and management
 * - Displaying a list of assets for the selected client
 * - Adding, editing, and deleting assets
 * - Launching the asset form when needed
 * - CSV/Excel import functionality
 * 
 * This is the parent component that is loaded when a user clicks on the
 * "Crown Jewels" option in the sidebar navigation.
 * 
 * Dependencies:
 * - React
 * - CrownJewelAssetForm component for adding/editing assets
 * - ImportWizard component for CSV/Excel imports
 * 
 * Props:
 * @param {string} newClientName - Current value of new client input
 * @param {function} setNewClientName - State setter for new client name
 * @param {array} clients - List of available clients
 * @param {string} selectedClient - ID of currently selected client
 * @param {function} setSelectedClient - State setter for selected client
 * @param {function} handleAddClient - Function to add a new client
 * @param {boolean} showAssetForm - Whether to show the asset form
 * @param {function} setShowAssetForm - State setter for showing asset form
 * @param {boolean} isEditMode - Whether form is in edit mode
 * @param {string} editAssetId - ID of asset being edited
 * @param {array} crownJewelAssets - List of all crown jewel assets
 * @param {function} handleAddAsset - Function to initiate adding a new asset
 * @param {function} handleSaveAsset - Function to save an asset (new or edited)
 * @param {function} handleCancelAssetForm - Function to cancel asset form
 * @param {function} handleEditAsset - Function to edit an existing asset
 * @param {function} handleDeleteAsset - Function to delete an asset
 */
import React, { useState, useEffect } from 'react';
import CrownJewelAssetForm from './CrownJewelAssetForm';
import KeyControlsHeatMap from './KeyControlsHeatMap';
import ClientMetrics from './ClientMetrics';
import AssetReviewSettings from './AssetReviewSettings';
import ImportWizard from './Import/ImportWizard';
import { v4 as uuidv4 } from 'uuid';

// Move these helper functions outside of components
// More comprehensive function to find owner in asset data
const getOwner = (asset) => {
  // First check for exact owner property matches
  if (asset.owner) return asset.owner;
  if (asset.assetOwner) return asset.assetOwner;
  if (asset.ownerName) return asset.ownerName;
  if (asset.basicInfo && asset.basicInfo.owner) return asset.basicInfo.owner;
  if (asset.basicInfo && asset.basicInfo.assetOwner) return asset.basicInfo.assetOwner;
  if (asset.securityInfo && asset.securityInfo.owner) return asset.securityInfo.owner;
  
  // For debugging, log all properties that might be relevant
  console.log('Finding owner in asset:', asset.id);
  
  // Specifically look for properties that ONLY contain 'owner' and not 'department'
  for (const [key, value] of Object.entries(asset)) {
    // Skip any keys that contain "department" to avoid confusion
    if (key.toLowerCase().includes('department')) continue;
    
    if (key.toLowerCase().includes('owner') && typeof value === 'string' && value.trim()) {
      console.log(`Found owner in property ${key}: ${value}`);
      return value;
    }
    
    // Check nested objects, but again skip department-related properties
    if (typeof value === 'object' && value !== null) {
      for (const [nestedKey, nestedValue] of Object.entries(value)) {
        // Skip department related keys
        if (nestedKey.toLowerCase().includes('department')) continue;
        
        if (nestedKey.toLowerCase().includes('owner') && typeof nestedValue === 'string' && nestedValue.trim()) {
          console.log(`Found owner in nested property ${key}.${nestedKey}: ${nestedValue}`);
          return nestedValue;
        }
      }
    }
  }
  
  return '-';
};

// More comprehensive function to find department in asset data
const getDepartment = (asset) => {
  // Check all possible locations where department might be stored
  if (asset.department) return asset.department;
  if (asset.assetDepartment) return asset.assetDepartment;
  if (asset.basicInfo && asset.basicInfo.department) return asset.basicInfo.department;
  if (asset.basicInfo && asset.basicInfo.assetDepartment) return asset.basicInfo.assetDepartment;
  if (asset.securityInfo && asset.securityInfo.department) return asset.securityInfo.department;
  if (asset.securityInfo && asset.securityInfo.assetDepartment) return asset.securityInfo.assetDepartment;
  
  // Try to get any property that might contain "department" in its name
  for (const [key, value] of Object.entries(asset)) {
    if (key.toLowerCase().includes('department') && typeof value === 'string' && value.trim()) {
      return value;
    }
    
    // Check nested objects
    if (typeof value === 'object' && value !== null) {
      for (const [nestedKey, nestedValue] of Object.entries(value)) {
        if (nestedKey.toLowerCase().includes('department') && typeof nestedValue === 'string' && nestedValue.trim()) {
          return nestedValue;
        }
      }
    }
  }
  
  return '-';
};

const CrownJewelAssetManager = ({ 
  newClientName, 
  setNewClientName, 
  clients, 
  selectedClient, 
  setSelectedClient, 
  handleAddClient,
  showAssetForm,
  setShowAssetForm,
  isEditMode,
  setIsEditMode,
  editAssetId,
  setEditAssetId,
  crownJewelAssets,
  handleAddAsset,
  handleSaveAsset,
  handleCancelAssetForm,
  handleEditAsset,
  handleDeleteAsset,
  setCrownJewelAssets
}) => {
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'heatmap'
  const [editFromHeatmap, setEditFromHeatmap] = useState(false);
  const [reviewPeriodDays, setReviewPeriodDays] = useState(90);
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  
  // Add client form state
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientForm, setClientForm] = useState({
    company: '',
    contactName: '',
    contactEmail: ''
  });
  const [clientFormErrors, setClientFormErrors] = useState({});
  
  // Add state variables for new client modal
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [clientFormData, setClientFormData] = useState({
    name: '',
    contactName: '',
    contactEmail: '',
    contactPhone: ''
  });

  // Add this near the top with your other state declarations
  const [refreshKey, setRefreshKey] = useState(0);

  // Add this at the top where other state variables are defined
  const [localClients, setLocalClients] = useState([]);

  // Add this to your state variables
  const [currentView, setCurrentView] = useState('dashboard');

  // Add a refreshAssets state to trigger refreshes
  const [refreshAssets, setRefreshAssets] = useState(0);

  // Add state to track assets directly in this component
  const [localAssets, setLocalAssets] = useState([]);

  // Create a state variable to track which tab should be active
  const [activeFormTab, setActiveFormTab] = useState('basic');

  // Add a new state to track the form source
  const [formSource, setFormSource] = useState('default');

  // Make sure the selectedClient state exists too
  const [localSelectedClient, setLocalSelectedClient] = useState('');

  // Add this new state variable
  const [showAssetFormModal, setShowAssetFormModal] = useState(false);
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [assetFormInitialTab, setAssetFormInitialTab] = useState('basic');

  // Add this state at the top with other state variables
  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [editClientData, setEditClientData] = useState({
    id: '',
    name: '',
    contactName: '',
    contactEmail: '',
    contactPhone: ''
  });

  // First, add these new state variables at the top of your component with the other states
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [assetToDeleteInfo, setAssetToDeleteInfo] = useState({
    id: null,
    name: '',
    index: -1
  });

  // Add import wizard state
  const [showImportWizard, setShowImportWizard] = useState(false);

  // PRD v11: Replace individual panel states with single state
  const [panelsExpanded, setPanelsExpanded] = useState(true);

  // PRD v11: Add useEffect to manage panel state persistence
  useEffect(() => {
    const savedPanelState = localStorage.getItem('panelsExpanded');
    if (savedPanelState !== null) {
      setPanelsExpanded(JSON.parse(savedPanelState));
    }
  }, []);

  // PRD v11: Auto-collapse panels on small screens
  useEffect(() => {
    const handleResize = () => {
      const isSmallScreen = window.innerWidth < 1024; // lg breakpoint
      
      // Auto-collapse on small screens if not already manually set
      if (isSmallScreen && !localStorage.getItem('panelsExpanded')) {
        setPanelsExpanded(false);
      }
    };
    
    // Check on mount and resize
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // PRD v11: Add keyboard shortcuts for panel toggling
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + Space to toggle both panels
      if ((e.ctrlKey || e.metaKey) && e.key === ' ') {
        e.preventDefault();
        toggleBothPanels();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // PRD v11: Single toggle function for both panels
  const toggleBothPanels = () => {
    const newState = !panelsExpanded;
    setPanelsExpanded(newState);
    localStorage.setItem('panelsExpanded', JSON.stringify(newState));
  };

  // PRD v9: Dynamic asset form title function
  const getDynamicAssetFormTitle = () => {
    const asset = editingAssetId ? localAssets.find(a => a.id === editingAssetId) : null;
    const assetName = asset?.assetName || asset?.basicInfo?.name;
    const isEditing = !!editingAssetId;
    
    if (isEditing && assetName && assetName.trim() !== '') {
      return `Edit Asset: ${assetName}`;
    } else if (isEditing) {
      return 'Edit Crown Jewel Asset';
    } else {
      return 'Add New Crown Jewel Asset';
    }
  };

  // PRD v9: Collapsible Panel Component
  const CollapsiblePanel = ({ title, isCollapsed, onToggle, children, shortcutKey }) => {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={onToggle}
        >
          <div className="flex items-center">
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            {shortcutKey && (
              <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                Ctrl+{shortcutKey}
              </span>
            )}
          </div>
          <button className="text-gray-500 hover:text-gray-700 transition-colors">
            {isCollapsed ? (
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            )}
          </button>
        </div>
        
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isCollapsed ? 'max-h-0' : 'max-h-[1000px]'
        }`}>
          <div className="p-4 pt-0">
            {children}
          </div>
        </div>
      </div>
    );
  };

  // Helper function to get client name for dashboard title
  const getClientName = (clientId) => {
    const client = localClients.find(c => c.id === clientId);
    return client ? client.name : 'Selected Client';
  };

  // Add import-related handlers
  const handleOpenImport = () => {
    if (!selectedClient) {
      alert('Please select a client first before importing assets.');
      return;
    }
    setShowImportWizard(true);
  };

  const handleCloseImport = () => {
    setShowImportWizard(false);
    // Refresh assets after import
    setRefreshAssets(prev => prev + 1);
  };

  const handleImportComplete = (importStats) => {
    console.log('Import completed:', importStats);
    // Refresh the local assets after import
    setRefreshAssets(prev => prev + 1);
    // Optionally show success message
    if (importStats.successful > 0) {
      alert(`Successfully imported ${importStats.successful} assets!`);
    }
  };

  // Add useEffect to load assets when selected client changes or refresh is triggered
  useEffect(() => {
    if (selectedClient) {
      // Get all assets from localStorage
      const storedAssets = JSON.parse(localStorage.getItem('crownJewelAssets') || '[]');
      
      // Filter for current client
      const clientAssets = storedAssets.filter(asset => asset.clientId === selectedClient);
      
      // Update local state
      setLocalAssets(clientAssets);
    }
  }, [selectedClient, refreshAssets]);

  // Update the forceRefreshAssets function to be simpler
  const forceRefreshAssets = () => {
    // Just increment the refresh counter to trigger the useEffect
    setRefreshAssets(prev => prev + 1);
  };

  // Add this function to refresh assets without using setCrownJewelAssets
  const refreshAssetDisplay = () => {
    // Force a component re-render to refresh the UI
    setRefreshAssets(prev => prev + 1);
    
    // If any custom refresh logic is needed in the parent component,
    // call it here
    if (typeof onAssetsChanged === 'function') {
      onAssetsChanged();
    }
  };

  // Add this effect to sync the localClients with the clients prop
  useEffect(() => {
    if (clients && clients.length > 0) {
      setLocalClients(clients);
    } else {
      // Load from localStorage as fallback
      const storedClients = JSON.parse(localStorage.getItem('crownJewelClients') || '[]');
      setLocalClients(storedClients);
    }
  }, [clients]);

  // In the component, add this right after useEffect for loading assets
  useEffect(() => {
    // Debug log the first asset structure to see the actual data
    if (localAssets && localAssets.length > 0) {
      console.log('First asset structure:', JSON.stringify(localAssets[0], null, 2));
    }
  }, [localAssets]);

  /**
   * Get the criticality status color for use in the UI
   */
  const getCriticalityColor = (value) => {
    if (!value) return 'bg-gray-100 text-gray-800';
    
    if (value.includes('1-Extremely')) return 'bg-red-100 text-red-800';
    if (value.includes('2-Very High')) return 'bg-red-50 text-red-700';
    if (value.includes('3-High')) return 'bg-orange-100 text-orange-800';
    if (value.includes('4-Medium')) return 'bg-yellow-100 text-yellow-800';
    if (value.includes('5-Low')) return 'bg-green-100 text-green-800';
    
    return 'bg-gray-100 text-gray-800';
  };

  /**
   * Filter assets for the currently selected client
   */
  const filteredAssets = selectedClient 
    ? crownJewelAssets.filter(asset => asset.clientId === selectedClient) 
    : [];

  // Completely rewrite the editAssetHandler function to be very explicit
  const editAssetHandler = (assetId) => {
    console.log('Edit button clicked in asset list for asset ID:', assetId);
    
    // IMPORTANT: First set initialTab to basic BEFORE setting the editingAssetId
    // This order is crucial for the form to recognize the correct tab
    setAssetFormInitialTab('basic');
    
    // Clear any previous asset ID
    setEditingAssetId(null);
    
    // Use a small delay to ensure state update happens before setting the asset ID
    setTimeout(() => {
      // Now set the editing asset ID after the tab has been set
      setEditingAssetId(assetId);
      
      // Finally show the modal
      setShowAssetFormModal(true);
    }, 10);
    
    // If there's a parent handler, call it
    if (handleEditAsset) {
      handleEditAsset(assetId);
    }
  };

  // Update the getEditAssetData function to use our localAssets state first
  const getEditAssetData = () => {
    if (!editAssetId) return {};
    
    // First try to get the asset from our localAssets state
    const assetFromState = localAssets.find(asset => asset.id === editAssetId);
    if (assetFromState) {
      return assetFromState;
    }
    
    // If not found in state, try localStorage as fallback
    const storedAssets = JSON.parse(localStorage.getItem('crownJewelAssets') || '[]');
    const assetFromStorage = storedAssets.find(asset => asset.id === editAssetId);
    
    return assetFromStorage || {};
  };

  /**
   * Export crown jewel assets for the selected client to a JSON file
   */
  const handleExportAssets = () => {
    if (!selectedClient || filteredAssets.length === 0) return;
    
    // Get client data for the filename
    const clientData = clients.find(c => c.id === selectedClient);
    
    // Use company name for the filename instead of client name
    const companyName = clientData?.company || clientData?.name || 'client';
    const fileName = `crown_jewels_${companyName.replace(/\s+/g, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.json`;
    
    // Create a JSON blob with the data
    const exportData = {
      clientId: selectedClient,
      clientName: clientData?.name,
      companyName: clientData?.company,
      exportDate: new Date().toISOString(),
      assets: filteredAssets
    };
    
    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    
    // Create a download link and trigger it
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /**
   * Import crown jewel assets from a JSON file
   */
  const handleImportAssets = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    try {
      const fileContent = await file.text();
      const importData = JSON.parse(fileContent);
      
      // Basic validation
      if (!importData.assets || !Array.isArray(importData.assets)) {
        alert('Invalid import file format. Missing assets array.');
        return;
      }
      
      // Confirm import
      const confirmImport = window.confirm(
        `Import ${importData.assets.length} assets for ${importData.clientName || 'the selected client'}?`
      );
      
      if (confirmImport) {
        // Add timestamps to imported data
        const timestamp = new Date().toISOString();
        const processedAssets = importData.assets.map(asset => ({
          ...asset,
          clientId: selectedClient, // Ensure assets belong to selected client
          importedAt: timestamp,
          lastUpdated: asset.lastUpdated || timestamp
        }));
        
        // Call a parent function to process the import
        handleImportCrownJewels(processedAssets);
      }
    } catch (error) {
      console.error('Error importing assets:', error);
      alert('Error importing assets. Please check the file format.');
    }
    
    // Clear the file input
    event.target.value = null;
  };

  // Add this function to handle asset reviews
  const handleReviewAsset = (assetId) => {
    // Find the asset
    const asset = crownJewelAssets.find(a => a.id === assetId);
    if (!asset) return;
    
    // Update with review date
    const updatedAsset = {
      ...asset,
      lastReviewed: new Date().toISOString(),
      // Don't update lastUpdated since no data changed
    };
    
    // Save the asset
    handleSaveAsset(updatedAsset);
    
    // Add to activity log
    addActivityLog({
      type: 'review',
      assetId,
      assetName: asset.assetName,
      description: 'Asset reviewed with no changes',
      timestamp: new Date().toISOString(),
      user: currentUser?.name || 'User'
    });
  };

  // Filter for overdue assets
  const getOverdueAssets = (assets) => {
    return assets.filter(asset => {
      const lastReviewDate = asset.lastReviewed ? new Date(asset.lastReviewed) : null;
      const creationDate = asset.createdAt ? new Date(asset.createdAt) : new Date();
      
      // If never reviewed, use creation date as reference
      const referenceDate = lastReviewDate || creationDate;
      
      // Calculate days since last review
      const daysSinceReview = Math.floor((new Date() - referenceDate) / (1000 * 60 * 60 * 24));
      
      return daysSinceReview > reviewPeriodDays;
    });
  };

  // Add filter to assets in the list view
  const assetsToDisplay = showOverdueOnly 
    ? getOverdueAssets(filteredAssets)
    : filteredAssets;

  // Add client form validation to work with both forms
  const validateClientForm = (form) => {
    const errors = {};
    
    if (form.name) {
      // New client form validation
      if (!form.name.trim()) {
        errors.name = 'Client name is required';
      }
      
      if (!form.contactName.trim()) {
        errors.contactName = 'Contact name is required';
      }
      
      if (!form.contactEmail.trim()) {
        errors.contactEmail = 'Contact email is required';
      } else if (!/\S+@\S+\.\S+/.test(form.contactEmail)) {
        errors.contactEmail = 'Email address is invalid';
      }
    } else if (form.company) {
      // Existing client form validation
      if (!form.company.trim()) errors.company = 'Company name is required';
      if (!form.contactName.trim()) errors.contactName = 'Contact full name is required';
      if (!form.contactEmail.trim()) errors.contactEmail = 'Email is required';
      if (form.contactEmail && !/^\S+@\S+\.\S+$/.test(form.contactEmail)) {
        errors.contactEmail = 'Email is invalid';
      }
    }
    
    return errors;
  };
  
  // Updated client creation function to prevent duplicates
  const handleAddNewClient = (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateClientForm(clientFormData);
    if (Object.keys(errors).length > 0) {
      setClientFormErrors(errors);
      return;
    }
    
    try {
      // Check if client name already exists (case-insensitive)
      const clientNameExists = clients.some(
        client => client.name.toLowerCase() === clientFormData.name.toLowerCase()
      );
      
      if (clientNameExists) {
        alert(`A client with the name "${clientFormData.name}" already exists. Please use a different name.`);
        return;
      }
      
      // Create new client object with a unique ID
      const newClientId = 'client_' + Date.now();
      const newClient = {
        id: newClientId,
        name: clientFormData.name,
        contactName: clientFormData.contactName,
        contactEmail: clientFormData.contactEmail,
        contactPhone: clientFormData.contactPhone || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Get current clients from localStorage to ensure we're working with latest data
      const existingClients = JSON.parse(localStorage.getItem('crownJewelClients') || '[]');
      
      // Add new client to the array
      const updatedClients = [...existingClients, newClient];
      
      // Update localStorage
      localStorage.setItem('crownJewelClients', JSON.stringify(updatedClients));
      
      // Update local state (NOT setClients)
      setLocalClients(updatedClients);
      
      // Reset form and close modal
      setClientFormData({
        name: '',
        contactName: '',
        contactEmail: '',
        contactPhone: ''
      });
      setClientFormErrors({});
      setShowNewClientModal(false);
      
      // Force a re-render
      setRefreshKey(prevKey => prevKey + 1);
      
    } catch (error) {
      console.error("Error adding client:", error);
      alert("An error occurred while adding the client. Please try again.");
    }
  };

  // Add a function to fix any duplicate clients in localStorage
  const cleanupDuplicateClients = () => {
    try {
      // Get clients from localStorage
      const storedClients = JSON.parse(localStorage.getItem('crownJewelClients') || '[]');
      
      // Create a map to track seen client names (case-insensitive)
      const seenNames = new Map();
      const uniqueClients = [];
      
      // Keep only unique clients by name
      for (const client of storedClients) {
        const nameLower = client.name.toLowerCase();
        if (!seenNames.has(nameLower)) {
          seenNames.set(nameLower, true);
          uniqueClients.push(client);
        }
      }
      
      // If we removed duplicates, update localStorage
      if (uniqueClients.length !== storedClients.length) {
        localStorage.setItem('crownJewelClients', JSON.stringify(uniqueClients));
        console.log(`Cleaned up ${storedClients.length - uniqueClients.length} duplicate clients`);
        
        // Update local state (NOT setClients)
        setLocalClients(uniqueClients);
      }
      
      return uniqueClients;
    } catch (error) {
      console.error("Error cleaning up duplicates:", error);
      return [];
    }
  };

  // Add this to your component to clean up duplicates on load
  useEffect(() => {
    const cleanedClients = cleanupDuplicateClients();
    if (cleanedClients.length > 0) {
      // If you have access to setClients, update the state
      if (typeof setClients === 'function') {
        setClients(cleanedClients);
      }
    }
  }, []);

  // Add an effect to handle opening the client modal from the parent's Edit button
  useEffect(() => {
    // Function to handle edit client requests from parent component
    const handleEditClientRequest = () => {
      if (selectedClient) {
        const client = clients.find(c => c.id === selectedClient);
        if (client) {
          setClientForm({
            company: client.name,
            contactName: client.contactName || '',
            contactEmail: client.contactEmail || ''
          });
          setShowClientModal(true);
        }
      }
    };

    // Add listener for custom "editClient" event
    window.addEventListener('editClient', handleEditClientRequest);
    
    // Add click listener to the edit button
    const editClientButton = document.querySelector('button[aria-label="Edit Client"]');
    if (editClientButton) {
      editClientButton.addEventListener('click', handleEditClientRequest);
    }
    
    return () => {
      window.removeEventListener('editClient', handleEditClientRequest);
      if (editClientButton) {
        editClientButton.removeEventListener('click', handleEditClientRequest);
      }
    };
  }, [selectedClient, clients]);

  // Completely rewrite the client deletion function
  const handleDeleteClient = () => {
    // Find the client to delete
    const clientToDelete = localClients.find(c => c.id === selectedClient);
    if (!clientToDelete) {
      alert("Please select a client first");
      return;
    }
    
    // Show confirmation dialog
    if (!window.confirm(`Are you sure you want to delete client "${clientToDelete.name}"?\nThis will permanently remove all associated assets.`)) {
      return;
    }
    
    try {
      // Store client ID for reference
      const clientId = selectedClient;
      const clientName = clientToDelete.name;
      
      // Clear selection first to avoid state issues
      setSelectedClient('');
      
      // Update clients array
      const updatedClients = localClients.filter(client => client.id !== clientId);
      
      // Update localStorage
      localStorage.setItem('crownJewelClients', JSON.stringify(updatedClients));
      
      // Clean up assets in localStorage
      if (crownJewelAssets) {
        const updatedAssets = crownJewelAssets.filter(asset => asset.clientId !== clientId);
        localStorage.setItem('crownJewelAssets', JSON.stringify(updatedAssets));
      }
      
      // Update our local state (which we DO have access to)
      setLocalClients(updatedClients);
      
      // Show success message
      alert(`Client "${clientName}" has been deleted successfully.`);
      
    } catch (error) {
      console.error("Error deleting client:", error);
      alert("An error occurred while deleting the client. Please try again.");
    }
  };

  // Add this effect to restore the module state after reload
  useEffect(() => {
    // Check if we need to restore state after reload
    const lastActiveModule = localStorage.getItem('lastActiveModule');
    const lastSelectedClient = localStorage.getItem('lastSelectedClient');
    
    if (lastActiveModule === 'CJ' && lastSelectedClient) {
      // Restore selected client
      setSelectedClient(lastSelectedClient);
      
      // Clear the stored state to avoid issues on future visits
      localStorage.removeItem('lastActiveModule');
      localStorage.removeItem('lastActiveCJSection');
      localStorage.removeItem('lastSelectedClient');
    }
  }, []);

  // Add or update the asset form modal styling
  const assetFormModalStyle = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'white',
    padding: '2rem',
    borderRadius: '0.5rem',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    zIndex: 1000,
    maxWidth: '800px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto'
  };

  const assetFormOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 999
  };

  // Only keep the function to call after form operations to set the view
  const navigateToDashboard = () => {
    setCurrentView('dashboard');
  };

  // Add this style definition near the top of your component
  const assetFormStyles = `
    /* Form Styles */
    .fixed-height-tabs .tab-content {
      min-height: 450px;
      height: 100%;
    }
    
    .fixed-height-tabs .tab-panel {
      padding: 1rem;
      height: 100%;
    }

    /* PRD v12: Add styles for sticky table headers */
    .crown-jewels-table-container {
      scrollbar-width: thin;
      scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
    }
    
    .crown-jewels-table-container::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    
    .crown-jewels-table-container::-webkit-scrollbar-track {
      background: transparent;
    }
    
    .crown-jewels-table-container::-webkit-scrollbar-thumb {
      background-color: rgba(156, 163, 175, 0.5);
      border-radius: 4px;
    }
    
    .crown-jewels-table-header {
      transition: box-shadow 0.2s ease-in-out;
    }
    
    .crown-jewels-table-header.scrolled {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    /* PRD v13: Add styles for sortable headers */
    .sortable-header {
      cursor: pointer;
      user-select: none;
      transition: background-color 0.15s ease;
    }

    .sortable-header:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }

    .sortable-header.active {
      font-weight: 600;
      color: #3B82F6;
    }

    .sort-indicator {
      display: inline-block;
      margin-left: 0.5rem;
      opacity: 0.5;
    }

    .active .sort-indicator {
      opacity: 1;
    }
  `;

  // COMPLETELY REBUILT ASSET DELETION WITH CUSTOM CONFIRMATION DIALOG
  const deleteAssetHandler = (assetId) => {
    console.log("Delete handler called with ID:", assetId);
    
    // Get all assets from localStorage
    const allAssets = JSON.parse(localStorage.getItem('crownJewelAssets') || '[]');
    console.log("Total assets in storage:", allAssets.length);
    
    // Find the asset to delete
    const assetToDelete = allAssets.find(asset => String(asset.id) === String(assetId));
    
    if (!assetToDelete) {
      console.error("Asset not found with ID:", assetId);
      return;
    }
    
    // Get asset name for the confirmation dialog
    const assetName = assetToDelete.assetName || 
                      assetToDelete.applicationSystemName || 
                      'Unnamed Asset';
    
    // Set the asset info for the confirmation dialog
    setAssetToDeleteInfo({
      id: assetId,
      name: assetName
    });
    
    // Show the custom confirmation dialog
    setShowDeleteConfirmation(true);
  };

  // ABSOLUTE BARE MINIMUM DELETE FUNCTION - NO COMPLEXITY
  const confirmAssetDeletion = () => {
    console.log("========== SUPER SIMPLE DELETE ==========");
    const { id: assetId } = assetToDeleteInfo;
    
    // 1. Get assets from localStorage
    const allAssets = JSON.parse(localStorage.getItem('crownJewelAssets') || '[]');
    console.log("All assets before:", allAssets.length);
    
    // 2. Create new array without the deleted asset
    const updatedAssets = allAssets.filter(asset => {
      // Convert both to strings and compare exactly
      return String(asset.id) !== String(assetId);
    });
    
    console.log("Assets after filter:", updatedAssets.length);
    console.log("Removed:", allAssets.length - updatedAssets.length);
    
    // 3. Save back to localStorage
    localStorage.setItem('crownJewelAssets', JSON.stringify(updatedAssets));
    console.log("Updated localStorage");
    
    // 4. Get current client ID
    const currentClientId = selectedClient;
    console.log("Current client ID:", currentClientId);
    
    // 5. Get only THIS client's assets for the UI
    const clientAssets = updatedAssets.filter(asset => 
      String(asset.clientId) === String(currentClientId)
    );
    
    console.log("Client assets count:", clientAssets.length);
    
    // 6. EXPLICITLY set the entire client assets array
    setLocalAssets(clientAssets);
    console.log("Updated component state");
    
    // 7. Hide the dialog
    setShowDeleteConfirmation(false);
    
    // 8. Force a UI refresh after a short delay
    setTimeout(() => {
      console.log("Forcing UI refresh");
      forceRefreshAssets();
    }, 100);
    
    console.log("========== DELETE COMPLETE ==========");
  };

  // Fix the handleHeatmapEdit function
  const handleHeatmapEdit = (assetId) => {
    console.log('========== HEATMAP EDIT START ==========');
    console.log('Editing asset ID:', assetId);
    
    // Get current assets from localStorage
    const storedAssets = JSON.parse(localStorage.getItem('crownJewelAssets') || '[]');
    const assetToEdit = storedAssets.find(asset => 
      String(asset.id).trim() === String(assetId).trim()
    );
    
    if (!assetToEdit) {
      console.error('Asset not found for editing:', assetId);
      return;
    }
    
    console.log('Found asset to edit:', assetToEdit);
    
    // IMPORTANT: Set the editing ID first
    setEditingAssetId(assetId);
    console.log('Set editingAssetId to:', assetId);
    
    // Then set the initial tab
    setAssetFormInitialTab('controls');
    console.log('Set form initial tab to: controls');
    
    // Show the form with a slight delay to ensure state is updated
    setTimeout(() => {
      setShowAssetFormModal(true);
      console.log('Showing asset form modal');
      console.log('========== HEATMAP EDIT END ==========');
    }, 50);
  };

  // Add this function to handle view toggling that forces refresh
  const toggleView = (newView) => {
    // Update the view mode
    setViewMode(newView);
    
    // Force refresh of assets data when view changes
    setRefreshAssets(prev => prev + 1);
    
    console.log(`Switched to ${newView} view, refreshing data`);
  };

  // Update the useEffect to check for both showAssetForm, formSource AND isEditMode
  useEffect(() => {
    // Only apply the Key Controls tab selection when:
    // 1. Form is visible
    // 2. Source is heatmap
    // 3. We're in edit mode (not adding a new asset)
    if (showAssetForm && formSource === 'heatmap' && isEditMode) {
      // Force tab selection after form is visible
      setActiveFormTab('keyControls');
      
      // Click the Key Controls tab after a short delay
      setTimeout(() => {
        // Find and click the Key Controls tab button
        const tabButtons = document.querySelectorAll('button');
        for (const button of tabButtons) {
          if (button.textContent.includes('Key Controls')) {
            button.click();
            break;
          }
        }
      }, 200);
    }
  }, [showAssetForm, formSource, isEditMode]);

  // Add a new state for editing the review period
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [tempReviewPeriod, setTempReviewPeriod] = useState(90);

  // Add a new function to count overdue assets
  const countOverdueAssets = (assets, reviewPeriod) => {
    return assets.filter(asset => {
      const lastReviewDate = asset.lastReviewed ? new Date(asset.lastReviewed) : null;
      const creationDate = asset.createdAt ? new Date(asset.createdAt) : new Date();
      
      // If never reviewed, use creation date as reference
      const referenceDate = lastReviewDate || creationDate;
      
      // Calculate days since last review
      const daysSinceReview = Math.floor((new Date() - referenceDate) / (1000 * 60 * 60 * 24));
      
      return daysSinceReview > reviewPeriod;
    }).length;
  };

  // Add this function to calculate the last update time
  const getLastUpdateTime = () => {
    if (!localAssets || localAssets.length === 0) {
      return 'N/A';
    }

    // Find the most recent update date from all assets
    const dates = localAssets
      .map(asset => {
        // Try different date properties that might exist
        return asset.updatedAt || asset.lastUpdated || asset.createdAt || null;
      })
      .filter(date => date !== null)
      .map(dateStr => new Date(dateStr));

    if (dates.length === 0) {
      return 'N/A';
    }

    // Get the most recent date
    const mostRecentDate = new Date(Math.max(...dates));
    
    // Format the date for display
    return mostRecentDate.toLocaleString('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  };

  // Update the AssetRow component to use the moved functions
  const AssetRow = ({ asset, onEdit, onDelete }) => {
    // Function to format the date in a readable format
    const formatDate = (dateString) => {
      if (!dateString) return 'Never';
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? 'Never' : date.toLocaleDateString();
    };

    // Get criticality color class based on the value
    const getCriticalityClass = (criticality) => {
      if (!criticality) return 'bg-gray-100 text-gray-800';
      
      if (criticality.includes('1-Extremely')) return 'bg-red-100 text-red-800';
      if (criticality.includes('2-Very')) return 'bg-red-50 text-red-700';
      if (criticality.includes('3-High')) return 'bg-orange-100 text-orange-800';
      if (criticality.includes('4-Medium')) return 'bg-yellow-100 text-yellow-800';
      if (criticality.includes('5-Low')) return 'bg-green-100 text-green-800';
      
      return 'bg-gray-100 text-gray-800';
    };

    return (
      <tr>
        <td className="px-3 py-2 whitespace-nowrap">
          <div 
            className="flex items-center group cursor-pointer"
            onClick={() => onEdit(asset.id)}
            title="Click to edit this asset"
          >
            <span className="text-sm font-medium text-blue-600 group-hover:text-blue-800 border-b border-blue-200 group-hover:border-blue-400">
              {asset.assetName || asset.basicInfo?.name || 'Unnamed Asset'}
            </span>
            
            {/* Add edit icon that appears on hover */}
            <span className="ml-2 text-gray-400 group-hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </span>
          </div>
        </td>
        <td className="px-3 py-2 text-sm text-gray-500">
          {asset.assetDescription || asset.description || asset.basicInfo?.description || '-'}
        </td>
        <td className="px-3 py-2 text-sm text-gray-500">
          {getOwner(asset)}
        </td>
        <td className="px-3 py-2 text-sm text-gray-500">
          {getDepartment(asset)}
        </td>
        <td className="px-3 py-2 whitespace-nowrap">
          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getCriticalityClass(asset.criticalityClassification || asset.securityInfo?.criticalityClassification)}`}>
            {asset.criticalityClassification || asset.securityInfo?.criticalityClassification || 'Unrated'}
          </span>
        </td>
        <td className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap">
          {formatDate(asset.lastReviewDate || asset.lastUpdated)}
        </td>
        <td className="px-3 py-2 text-right text-sm font-medium whitespace-nowrap">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(asset.id);
            }}
            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
            title="Delete this asset"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </td>
      </tr>
    );
  };

  // Modify the handleClientFormSubmit function to save directly to localStorage
  const handleClientFormSubmit = (e) => {
    e.preventDefault();
    console.log("Submitting client form:", clientForm);
    
    // Create the client object
    const newClient = {
      id: Date.now().toString(),
      name: clientForm.company,
      contactName: clientForm.contactName,
      contactEmail: clientForm.contactEmail,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    // Save directly to localStorage first to ensure it's stored
    const existingClients = JSON.parse(localStorage.getItem('crownJewelClients') || '[]');
    const updatedClients = [...existingClients, newClient];
    localStorage.setItem('crownJewelClients', JSON.stringify(updatedClients));
    
    // Then call the prop function (as a backup)
    handleAddClient(newClient);
    
    // Update local state to refresh the dropdown
    setLocalClients(updatedClients);
    
    // Select the new client
    setSelectedClient(newClient.id);
    
    // Reset form and close modal
    setClientForm({
      company: '',
      contactName: '',
      contactEmail: ''
    });
    setShowClientModal(false);
    
    console.log("Client saved:", newClient);
    console.log("Updated clients:", updatedClients);

    // Force a refresh of the component
    setTimeout(() => {
      // This will force the component to re-render
      setRefreshKey(prev => prev + 1);
    }, 100);
  };

  // Add this handler function for the Add Asset button
  const handleAddAssetClick = () => {
    if (!selectedClient) {
      alert("Please select a client first before adding an asset.");
      return;
    }
    
    console.log("Add Asset button clicked for client:", selectedClient);
    setEditingAssetId(null);
    setShowAssetFormModal(true);
    setAssetFormInitialTab('basic');
  };

  // Complete rewrite of handleSaveAssetToStorage to fix editing issues
  const handleSaveAssetToStorage = (formData) => {
    console.log('Saving asset with data:', formData);
    console.log('Current editingAssetId:', editingAssetId);
    
    // Get all existing assets from localStorage
    const existingAssets = JSON.parse(localStorage.getItem('crownJewelAssets') || '[]');
    
    // If editing an existing asset (editingAssetId is set)
    if (editingAssetId) {
      console.log('UPDATING EXISTING ASSET with ID:', editingAssetId);
      
      // Find the index of the asset to update
      const assetIndex = existingAssets.findIndex(asset => 
        String(asset.id) === String(editingAssetId)
      );
      
      if (assetIndex !== -1) {
        console.log('Found asset at index:', assetIndex);
        
        // Create updated asset with preserved ID
        const updatedAsset = {
          ...formData,
          id: editingAssetId, // CRITICAL: Preserve the original ID
          lastUpdated: new Date().toISOString()
        };
        
        console.log('Updated asset:', updatedAsset);
        
        // Replace the asset in the array
        existingAssets[assetIndex] = updatedAsset;
        
        // Save back to localStorage
        localStorage.setItem('crownJewelAssets', JSON.stringify(existingAssets));
        console.log('Asset updated successfully');
        
        // Update local state
        setLocalAssets(prevAssets => {
          const newAssets = [...prevAssets];
          const localIndex = newAssets.findIndex(asset => 
            String(asset.id) === String(editingAssetId)
          );
          
          if (localIndex !== -1) {
            newAssets[localIndex] = updatedAsset;
          }
          
          return newAssets;
        });
      } else {
        console.error('Could not find asset with ID:', editingAssetId);
      }
    } else {
      // Creating a new asset
      console.log('CREATING NEW ASSET');
      
      // Generate a unique ID with timestamp and random string
      const newId = 'asset_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      console.log('Generated new asset ID:', newId);
      
      // Create the new asset
      const newAsset = {
        ...formData,
        id: newId,
        clientId: selectedClient,
        createdAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      };
      
      // Add to existing assets
      existingAssets.push(newAsset);
      
      // Save to localStorage
      localStorage.setItem('crownJewelAssets', JSON.stringify(existingAssets));
      console.log('New asset saved successfully');
      
      // Update local state
      setLocalAssets(prevAssets => [...prevAssets, newAsset]);
    }
    
    // Reset editing state
    setEditingAssetId(null);
    
    // Close the form
    setShowAssetFormModal(false);
    
    // Force refresh to update UI
    setTimeout(forceRefreshAssets, 100);
  };

  // Add this function to handle canceling the asset form
  const handleLocalCancelAssetForm = () => {
    setShowAssetFormModal(false);
  };

  // Add this function to handle opening the edit client modal
  const handleEditClient = () => {
    // Find the current client
    const currentClient = localClients.find(c => c.id === selectedClient);
    
    if (currentClient) {
      // Set the edit client data
      setEditClientData({
        id: currentClient.id,
        name: currentClient.name || '',
        contactName: currentClient.contactName || '',
        contactEmail: currentClient.contactEmail || '',
        contactPhone: currentClient.contactPhone || ''
      });
      
      // Show the edit modal
      setShowEditClientModal(true);
    }
  };

  // Add this function to handle saving the edited client
  const handleSaveEditedClient = (e) => {
    e.preventDefault();
    
    // Get all clients from localStorage
    const allClients = JSON.parse(localStorage.getItem('crownJewelClients') || '[]');
    
    // Update the client data
    const updatedClients = allClients.map(client => 
      client.id === editClientData.id ? editClientData : client
    );
    
    // Save to localStorage
    localStorage.setItem('crownJewelClients', JSON.stringify(updatedClients));
    
    // Update local state
    setLocalClients(updatedClients);
    
    // Close the modal
    setShowEditClientModal(false);
  };

  // Add this function to properly count critical assets (with criticality "1-Extremely")
  const countCriticalAssets = (assets) => {
    if (!assets || assets.length === 0) return 0;
    
    // Count only assets with criticality classification of "1-Extremely"
    return assets.filter(asset => 
      asset.criticalityClassification && 
      asset.criticalityClassification.includes("1-Extremely")
    ).length;
  };

  // Add scroll handler for table header shadow
  useEffect(() => {
    const handleScroll = (e) => {
      const header = document.querySelector('.crown-jewels-table-header');
      if (header) {
        if (e.target.scrollTop > 0) {
          header.classList.add('scrolled');
        } else {
          header.classList.remove('scrolled');
        }
      }
    };

    const container = document.querySelector('.crown-jewels-table-container');
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [viewMode]); // Re-run when view mode changes

  // Add sorting state
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: 'asc'
  });

  // Add sorting function
  const sortData = (data, sortConfig) => {
    if (!sortConfig.column) return data;

    return [...data].sort((a, b) => {
      switch (sortConfig.column) {
        case 'name':
          const nameA = (a.assetName || a.basicInfo?.name || '').toLowerCase();
          const nameB = (b.assetName || b.basicInfo?.name || '').toLowerCase();
          return sortConfig.direction === 'asc' 
            ? nameA.localeCompare(nameB, undefined, {sensitivity: 'base'})
            : nameB.localeCompare(nameA, undefined, {sensitivity: 'base'});

        case 'owner':
          const ownerA = getOwner(a).toLowerCase();
          const ownerB = getOwner(b).toLowerCase();
          return sortConfig.direction === 'asc'
            ? ownerA.localeCompare(ownerB, undefined, {sensitivity: 'base'})
            : ownerB.localeCompare(ownerA, undefined, {sensitivity: 'base'});

        case 'department':
          const deptA = getDepartment(a).toLowerCase();
          const deptB = getDepartment(b).toLowerCase();
          return sortConfig.direction === 'asc'
            ? deptA.localeCompare(deptB)
            : deptB.localeCompare(deptA);

        case 'criticality':
          const criticalityOrder = {
            '1-Extremely': 1,
            '2-Very': 2,
            '3-High': 3,
            '4-Medium': 4,
            '5-Low': 5,
            'Unrated': 6
          };
          const critA = a.criticalityClassification || a.securityInfo?.criticalityClassification || 'Unrated';
          const critB = b.criticalityClassification || b.securityInfo?.criticalityClassification || 'Unrated';
          const valueA = Object.entries(criticalityOrder).find(([key]) => critA.includes(key))?.[1] || 999;
          const valueB = Object.entries(criticalityOrder).find(([key]) => critB.includes(key))?.[1] || 999;
          return sortConfig.direction === 'asc' ? valueA - valueB : valueB - valueA;

        case 'lastReviewed':
          const dateA = new Date(a.lastReviewDate || a.lastUpdated || 0);
          const dateB = new Date(b.lastReviewDate || b.lastUpdated || 0);
          return sortConfig.direction === 'asc' 
            ? dateA.getTime() - dateB.getTime()
            : dateB.getTime() - dateA.getTime();

        default:
          return 0;
      }
    });
  };

  // Add click handler for column headers
  const handleSort = (column) => {
    setSortConfig(prevConfig => {
      if (prevConfig.column === column) {
        // Toggle direction if same column
        if (prevConfig.direction === 'asc') {
          return { column, direction: 'desc' };
        }
        // Reset if already desc
        return { column: null, direction: 'asc' };
      }
      // New column, start with asc
      return { column, direction: 'asc' };
    });
  };

  // Add function to get sort indicator
  const getSortIndicator = (column) => {
    if (sortConfig.column !== column) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Add effect to restore sort preferences
  useEffect(() => {
    const savedSort = sessionStorage.getItem('crownJewelsSort');
    if (savedSort) {
      setSortConfig(JSON.parse(savedSort));
    }
  }, []);

  // Add effect to save sort preferences
  useEffect(() => {
    if (sortConfig.column) {
      sessionStorage.setItem('crownJewelsSort', JSON.stringify(sortConfig));
    } else {
      sessionStorage.removeItem('crownJewelsSort');
    }
  }, [sortConfig]);

  // Add keyboard handler for column headers
  const handleHeaderKeyDown = (e, column) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSort(column);
    }
  };

  return (
    <>
      {/* Add the styles */}
      <style>{assetFormStyles}</style>
      
      <div className="flex flex-col h-full">
        {/* Fixed top section - won't scroll */}
        <div className="px-4 py-4">
          {/* PRD v9: Top section with Collapsible Client Management and Dashboard panels */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {/* PRD v9: Collapsible Client Management Panel - LEFT */}
            <CollapsiblePanel 
              title="Client Management"
              isCollapsed={!panelsExpanded}
              onToggle={toggleBothPanels}
              shortcutKey="Space"
            >
              {/* Client Dropdown */}
              <div className="mb-4">
                <select 
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                >
                  <option value="">Select Client</option>
                  {localClients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Client Info Card - Only show when a client is selected */}
              {selectedClient && (
                <div className="mb-4 bg-gray-50 p-3 rounded-md border border-gray-100">
                  {/* Get the selected client data */}
                  {(() => {
                    const client = localClients.find(c => c.id === selectedClient) || {};
                    return (
                      <>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            <h4 className="font-medium text-gray-800">{client.name}</h4>
                          </div>
                          
                          {/* Edit button */}
                          <button 
                            onClick={handleEditClient}
                            className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-50"
                            title="Edit client information"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                        </div>
                        
                        {/* Rest of the client info */}
                        {client.contactName && (
                          <div className="flex items-center text-sm text-gray-600 mb-2 ml-1">
                            <svg className="w-4 h-4 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span>{client.contactName}</span>
                          </div>
                        )}
                        
                        {/* Email with Envelope Icon */}
                        {client.contactEmail && (
                          <div className="flex items-center text-sm text-gray-600 mb-2 ml-1">
                            <svg className="w-4 h-4 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <span>{client.contactEmail}</span>
                          </div>
                        )}
                        
                        {/* Phone with Phone Icon - if available */}
                        {client.contactPhone && (
                          <div className="flex items-center text-sm text-gray-600 ml-1">
                            <svg className="w-4 h-4 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <span>{client.contactPhone}</span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
              
              {/* Client Actions */}
              <div className="grid grid-cols-1 gap-2">
                <div className="flex gap-2">
                  <button 
                    onClick={() => {
                      console.log("Add Client button clicked");
                      setShowClientModal(true);
                    }}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded flex items-center justify-center"
                  >
                    <svg className="w-4 h-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Add Client
                  </button>
                  
                  <button 
                    onClick={() => handleDeleteClient()}
                    className="flex-1 text-red-600 hover:bg-red-50 border border-red-200 px-3 py-2 rounded flex items-center justify-center"
                    disabled={!selectedClient}
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete Client
                  </button>
                </div>
              </div>
            </CollapsiblePanel>
            
            {/* PRD v9: Collapsible Client Dashboard Panel - RIGHT */}
            <CollapsiblePanel 
              title={`Client Dashboard: ${getClientName(selectedClient)}`}
              isCollapsed={!panelsExpanded}
              onToggle={toggleBothPanels}
              shortcutKey="Space"
            >
              <div className="grid grid-cols-2 gap-3">
                {/* CARD 1: Total Assets */}
                <div className="bg-gray-100 p-3 rounded">
                  <h3 className="text-gray-500 text-sm font-medium">TOTAL ASSETS</h3>
                  <p className="text-2xl font-bold">{localAssets.length}</p>
                </div>
                
                {/* CARD 2: Critical Assets */}
                <div className="bg-gray-100 p-3 rounded">
                  <h3 className="text-gray-500 text-sm font-medium">CRITICAL ASSETS</h3>
                  <p className="text-2xl font-bold text-red-600">
                    {countCriticalAssets(localAssets)}
                  </p>
                </div>
                
                {/* CARD 3: Last Updated */}
                <div className="bg-gray-100 p-3 rounded">
                  <h3 className="text-gray-500 text-sm font-medium">LAST UPDATED</h3>
                  <p className="text-md">{getLastUpdateTime()}</p>
                </div>
                
                {/* CARD 4: Asset Review Period */}
                <div className="bg-gray-100 p-3 rounded">
                  <div className="flex justify-between items-center">
                    <h3 className="text-gray-500 text-sm font-medium">REVIEW PERIOD</h3>
                    <button 
                      onClick={() => setShowReviewModal(true)}
                      className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                    >
                      Edit
                    </button>
                  </div>
                  <p className="text-md font-medium">{reviewPeriodDays} Days</p>
                  <p className="text-xs text-gray-600">
                    {countOverdueAssets(localAssets, reviewPeriodDays)} assets overdue for review
                  </p>
                </div>
              </div>
            </CollapsiblePanel>
          </div>
        </div>
        
        {/* Scrollable assets section */}
        <div className="px-4 pb-4 overflow-auto flex-1">
          {/* Assets Section with Tabs for List and Heatmap */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-800">Crown Jewel Assets</h3>
              <div className="flex space-x-3">
                <button 
                  onClick={handleOpenImport}
                  disabled={!selectedClient}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded flex items-center text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  title={!selectedClient ? "Please select a client first" : "Import assets from CSV/Excel file"}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  Import Assets
                </button>
                <button 
                  onClick={handleAddAssetClick}
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded flex items-center text-sm font-medium"
                >
                  <svg className="w-4 h-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Add Asset
                </button>
              </div>
            </div>
            
            {/* View Tabs */}
            <div className="border-b border-gray-200 mb-4">
              <nav className="-mb-px flex">
                <button
                  className={`py-2 px-4 border-b-2 font-medium text-sm ${
                    viewMode === 'list' 
                      ? 'border-blue-500 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  onClick={() => setViewMode('list')}
                >
                  Asset List
                </button>
                <button
                  className={`ml-8 py-2 px-4 border-b-2 font-medium text-sm ${
                    viewMode === 'heatmap' 
                      ? 'border-blue-500 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  onClick={() => setViewMode('heatmap')}
                >
                  Control Heatmap
                </button>
              </nav>
            </div>
            
            {/* Asset views - ensure AssetRow is defined before this point */}
            {viewMode === 'list' ? (
              <div className="overflow-x-auto max-h-[calc(100vh-400px)] crown-jewels-table-container">
                {localAssets.length > 0 ? (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-white sticky top-0 z-10 crown-jewels-table-header">
                      <tr className="transition-shadow" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        <th 
                          className={`px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-white sortable-header ${sortConfig.column === 'name' ? 'active' : ''}`}
                          onClick={() => handleSort('name')}
                          onKeyDown={(e) => handleHeaderKeyDown(e, 'name')}
                          role="columnheader"
                          aria-sort={sortConfig.column === 'name' ? sortConfig.direction : 'none'}
                          tabIndex={0}
                        >
                          <span className="flex items-center">
                            Application/System Name
                            <span className="sort-indicator" aria-hidden="true">{getSortIndicator('name')}</span>
                          </span>
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-white">
                          Description
                        </th>
                        <th 
                          className={`px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-white sortable-header ${sortConfig.column === 'owner' ? 'active' : ''}`}
                          onClick={() => handleSort('owner')}
                          onKeyDown={(e) => handleHeaderKeyDown(e, 'owner')}
                          role="columnheader"
                          aria-sort={sortConfig.column === 'owner' ? sortConfig.direction : 'none'}
                          tabIndex={0}
                        >
                          <span className="flex items-center">
                            Owner
                            <span className="sort-indicator" aria-hidden="true">{getSortIndicator('owner')}</span>
                          </span>
                        </th>
                        <th 
                          className={`px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-white sortable-header ${sortConfig.column === 'department' ? 'active' : ''}`}
                          onClick={() => handleSort('department')}
                          onKeyDown={(e) => handleHeaderKeyDown(e, 'department')}
                          role="columnheader"
                          aria-sort={sortConfig.column === 'department' ? sortConfig.direction : 'none'}
                          tabIndex={0}
                        >
                          <span className="flex items-center">
                            Department
                            <span className="sort-indicator" aria-hidden="true">{getSortIndicator('department')}</span>
                          </span>
                        </th>
                        <th 
                          className={`px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-white sortable-header ${sortConfig.column === 'criticality' ? 'active' : ''}`}
                          onClick={() => handleSort('criticality')}
                          onKeyDown={(e) => handleHeaderKeyDown(e, 'criticality')}
                          role="columnheader"
                          aria-sort={sortConfig.column === 'criticality' ? sortConfig.direction : 'none'}
                          tabIndex={0}
                        >
                          <span className="flex items-center">
                            Criticality
                            <span className="sort-indicator" aria-hidden="true">{getSortIndicator('criticality')}</span>
                          </span>
                        </th>
                        <th 
                          className={`px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-white sortable-header ${sortConfig.column === 'lastReviewed' ? 'active' : ''}`}
                          onClick={() => handleSort('lastReviewed')}
                          onKeyDown={(e) => handleHeaderKeyDown(e, 'lastReviewed')}
                          role="columnheader"
                          aria-sort={sortConfig.column === 'lastReviewed' ? sortConfig.direction : 'none'}
                          tabIndex={0}
                        >
                          <span className="flex items-center">
                            Last Reviewed
                            <span className="sort-indicator" aria-hidden="true">{getSortIndicator('lastReviewed')}</span>
                          </span>
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-white">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortData(localAssets, sortConfig).map(asset => (
                        <AssetRow 
                          key={asset.id} 
                          asset={asset} 
                          onEdit={editAssetHandler} 
                          onDelete={deleteAssetHandler}
                        />
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No assets found. Add your first asset to get started.
                  </div>
                )}
              </div>
            ) : (
              <KeyControlsHeatMap 
                assets={localAssets}
                onEdit={handleHeatmapEdit}
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Modals and other components... */}
      {showClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">Add New Client</h2>
            
            <form onSubmit={handleClientFormSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Client Name
                </label>
                <input
                  type="text"
                  value={clientForm.company || ''}
                  onChange={(e) => setClientForm({...clientForm, company: e.target.value})}
                  className="w-full p-2 border rounded-md"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Contact Name
                </label>
                <input
                  type="text"
                  value={clientForm.contactName || ''}
                  onChange={(e) => setClientForm({...clientForm, contactName: e.target.value})}
                  className="w-full p-2 border rounded-md"
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={clientForm.contactEmail || ''}
                  onChange={(e) => setClientForm({...clientForm, contactEmail: e.target.value})}
                  className="w-full p-2 border rounded-md"
                />
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowClientModal(false)}
                  className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  Save Client
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Asset Form Modal - Fixed size version */}
      {showAssetFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-hidden">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl m-4 flex flex-col" style={{ height: "calc(90vh - 2rem)" }}>
            {/* Fixed header */}
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold asset-form-modal-title">
                {getDynamicAssetFormTitle()}
              </h2>
            </div>
            
            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto p-6">
              <CrownJewelAssetForm
                clientId={selectedClient}
                onSave={handleSaveAssetToStorage}
                onCancel={handleLocalCancelAssetForm}
                initialData={editingAssetId ? localAssets.find(asset => asset.id === editingAssetId) : null}
                initialTab={assetFormInitialTab}
                isModal={true}
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Client Modal */}
      {showEditClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">Edit Client</h2>
            
            <form onSubmit={handleSaveEditedClient}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Client Name
                </label>
                <input
                  type="text"
                  value={editClientData.name}
                  onChange={(e) => setEditClientData({...editClientData, name: e.target.value})}
                  className="w-full p-2 border rounded-md"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Contact Name
                </label>
                <input
                  type="text"
                  value={editClientData.contactName}
                  onChange={(e) => setEditClientData({...editClientData, contactName: e.target.value})}
                  className="w-full p-2 border rounded-md"
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={editClientData.contactEmail}
                  onChange={(e) => setEditClientData({...editClientData, contactEmail: e.target.value})}
                  className="w-full p-2 border rounded-md"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Contact Phone
                </label>
                <input
                  type="text"
                  value={editClientData.contactPhone}
                  onChange={(e) => setEditClientData({...editClientData, contactPhone: e.target.value})}
                  className="w-full p-2 border rounded-md"
                />
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditClientModal(false)}
                  className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Custom Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0 bg-red-100 rounded-full p-2">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Delete Asset</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Are you sure you want to delete "{assetToDeleteInfo.name}"? This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={() => setShowDeleteConfirmation(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                onClick={confirmAssetDeletion}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Client Metrics / Dashboard Section */}
      {viewMode === 'dashboard' && selectedClient && (
        <div className="mb-8">
          <ClientMetrics 
            assets={localAssets} 
            clientName={getClientName(selectedClient)}
            criticalAssetsCount={countCriticalAssets(localAssets)}
          />
        </div>
      )}

      {/* Import Wizard Modal */}
      {showImportWizard && (
        <ImportWizard
          isOpen={showImportWizard}
          onClose={handleCloseImport}
          clients={localClients}
          selectedClient={selectedClient}
          onImportComplete={handleImportComplete}
        />
      )}
    </>
  );
};

export default CrownJewelAssetManager; 